import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import Workbook from 'react-excel-workbook';
import { Button, Col, Row } from 'react-bootstrap';
import { Heading5, ToggleSwitch } from '../widgets';
import { API_DATETIME_FORMAT, DISPLAY_DATE_FORMAT2, LOGISTIC_PLAN_TYPE_ID } from '../../constants';
import { isPlannedOrTodate, isToday } from '../../helpers/common';
import { formatNumber, Unit } from '../../helpers/unit';
import { useParams } from 'react-router-dom';
import { getLogisticsHistory } from '../../actions/logistics';
import { updateConfiguration, userConfiguration, getUserConfiguration } from '../../actions/user';
import { getLoadQueueCount } from '../../actions/load';
import { getUserInfo } from '../../helpers/session';

const LogisticsHeader = ({ loads, selectedName, NetTotal, totalTareWeight, selectedViewType }) => {
	const unit = Unit.getInstance();
	const seedSampleKeyName = 'seed_sample';
	const { totalCount } = useSelector(state => state.logistics.logisticsHistory);
	const { totalCount: tareTotalCount } = useSelector(state => state.logistics.tareLoadHistory.loadDetails);
	const { weightShortName } = useSelector(state => state.user.settings);
	const { range, rangeId, custom: { from, to }, timezone } = useSelector(state => state.analyze.searchParams);
	const farmName = useSelector(state => state.farm.currentFarm.farm_name);
	const farmSettings = useSelector(state => state.farm.farmSettings.farm_config);
	const autoRefresh = useSelector(state => state.user.configuration.autoRefresh);
	const selectedPlanTypeId = useSelector(state => state.plan.selectedPlanType);
	const farmConfig = useSelector(state => state.farm.farmSettings.farm_config);

	const { seed_load_sample: { configuration: { seed_load_sample_params = [] } = {} } = {} } = farmSettings || {};
	// const { load_geo_params: { configuration: { geo_custom_params = [] } = {} } = {} } = farmSettings || {};

	const dispatch = useDispatch();
	const params = useParams();

	const { farmId, crewId, egId, isTruck, type } = params;
	const logisticHistoryRefresh = autoRefresh && autoRefresh.logistic_history ? autoRefresh.logistic_history : false;

	let timerId = null;

	const getExcelFileName = () => {
		let dateRange = null;
		if (isPlannedOrTodate(range))
			dateRange = `${moment.utc().format(DISPLAY_DATE_FORMAT2)} to ${moment.utc().format(DISPLAY_DATE_FORMAT2)}`;
		else if (moment.utc(from).format(DISPLAY_DATE_FORMAT2) === moment.utc(to).format(DISPLAY_DATE_FORMAT2))
			dateRange = `${moment.utc(from).format(DISPLAY_DATE_FORMAT2)}`;
		else dateRange = `${moment.utc(from).format(DISPLAY_DATE_FORMAT2)} to ${moment.utc(to).format(DISPLAY_DATE_FORMAT2)}`;

		return `Logistics History_${farmName.replace(/(\W+)/gi, '_')}_${dateRange}.xlsx`;
	};

	const getNetTotal = useMemo(() => {
		return formatNumber(unit.toFixedOf(unit.toWeight(NetTotal, 2), 0));
	}, [NetTotal])

	const fileName = getExcelFileName();

	const makeSeedSamplesConcat = (body) => {
		let finalBody = [];
		body.forEach((each) => {
			if (each.hasOwnProperty(seedSampleKeyName)) {
				if (Array.isArray(each[seedSampleKeyName]) && each[seedSampleKeyName] && each[seedSampleKeyName].length > 0) {
					let seedSampleObject = {};
					each[seedSampleKeyName].forEach((sample, index) => {
						for (let x in sample) {
							if (sample[x] && x !== 'id') {
								seedSampleObject[x] = seedSampleObject[x] || '';
								seedSampleObject[x] += (index === each[seedSampleKeyName].length - 1) ? sample[x].toString() : sample[x].toString() + ", "
							}
						}
					})
					finalBody.push({ ...each, ...seedSampleObject })
				} else {
					finalBody.push(each);
				}
			}
		})
		return finalBody;
	}

	const addSeedSampleToHeader = (header, type) => {
		if (seed_load_sample_params && seed_load_sample_params.length > 0 && type === "seedHistory") {
			seed_load_sample_params.forEach((each) => {
				if (each.visible && each.key !== 'id') {
					header.push({
						key: each.key,
						filter: null,
						label: each.label === "Variety" ? "Sample Variety" : each.label,
						isShow: each.visible
					})
				}
			})
		}
		// if (geo_custom_params && geo_custom_params.length > 0 && type === "seedHistory") {
		//     geo_custom_params.forEach(it => {
		//         if (it.visible && it.key !== "id") {
		//             header.push({
		//                 key: it.key.toLowerCase(),
		//                 filter: null,
		//                 label: it.label,
		//                 isShow: it.visible
		//             });
		//         }
		//     });
		// }
		header.push({
			key: "notes",
			filter: null,
			label: "Notes",
			isShow: true
		});
		return header.map((item, i) => <Workbook.Column key={i} value={item.key} label={item.label} />)
	}

	const getLoadCapacity = (value) => {
		value = (value && value.toString().includes(',')) ? value.replace(/,/g, '') : value;
		return formatNumber(unit.toFixedOf(unit.toWeight(value, 2), 0));
	}
	const getSafeSheetName = (name) => {
		return name.length > 31 ? name.substring(0, 31) : name;
	};
	const getWorkSheets = () => {
		const { isRequesting } = loads.seedHistory;
		if (!isRequesting) {
			return Object.keys(loads).map(type => {
				let { title, header, body } = loads[type];
				header = header.filter((e) => e.key !== "action");
				let finalBody = [...body].map((e) => {
					const { field_geo_shape_properties: fieldGeoParams = {}, rate_info = {} } = e;
					const { load_params: loadParams = {} } = e;
					const { rate_value, rate_type_name, own_bed_rate_value, additional_surcharge_percent, surcharge_value, surcharge_type_name, total } = rate_info;
					let rateInfoCombined = `Rate: ${rate_value ? `$${rate_value} (${rate_type_name})` : '0'} | Surcharge: ${surcharge_value ? `$${surcharge_value} (${surcharge_type_name})` : '0'} | Own Bed: ${own_bed_rate_value ? `$${own_bed_rate_value}` : '0'} | Additional Surcharge: ${additional_surcharge_percent
						? `${additional_surcharge_percent} %` : '0'} | Total: ${total ? `$${unit.toFixedOf(total, 2)}` : '0'}`;
					return {
						...e,
						load_capacity: getLoadCapacity(e.load_capacity),
						load_gross_weight: getLoadCapacity(e.load_gross_weight),
						tare_weight: getLoadCapacity(e.tare_weight),
						light_weight: getLoadCapacity(e.light_weight),
						...fieldGeoParams,
						...loadParams,
						rate_info: rate_value ? rateInfoCombined : ''
					}
				})

				const limitedTitle = getSafeSheetName(title);
				finalBody = (type === "seedHistory") ? makeSeedSamplesConcat(finalBody) : finalBody;
				return <Workbook.Sheet key={type} data={finalBody} name={limitedTitle}>
					{addSeedSampleToHeader(header, type)}
				</Workbook.Sheet>;
			});
		}
	};
	const getLogisticsHistorylist = () => {
		const paramsObj = {
			farm_uuid: farmId,
			range_type: range,
			from_date: from,
			to_date: to,
			plan_type_id: 2,
			include_events: true,
		};

		if (type === 'crew' && crewId) paramsObj['crew_uuid'] = crewId
		if (isTruck === 'false' && egId) paramsObj['target_equipment_group_uuid'] = egId;
		if (isTruck === 'true' && egId) paramsObj['source_equipment_group_uuid'] = egId;

		dispatch(getLogisticsHistory(paramsObj));
	};

	const recentHours = useMemo(() => {
		let updatedHours;
		if (farmConfig) {
			updatedHours = farmConfig && farmConfig.load_queue_recent_hours &&
				farmConfig.load_queue_recent_hours.recent_hours ?
				farmConfig.load_queue_recent_hours.recent_hours : 0;
			return updatedHours;
		}
	}, [farmConfig]);

	const onRefresh = () => {
		const roleId = getUserInfo().role_id;

		let fromDate = null, toDate = null;
		if (!isPlannedOrTodate(range)) {
			if (from && to) {
				fromDate = isToday(range) ? moment.utc(from).subtract(recentHours, "hours").format(API_DATETIME_FORMAT) :
					moment.utc(from).format(API_DATETIME_FORMAT);
				toDate = moment.utc(to).format(API_DATETIME_FORMAT);
			} else {
				const now = moment.utc().tz(timezone);
				fromDate = now.clone().startOf("day").format(API_DATETIME_FORMAT);
				toDate = now.clone().endOf("day").format(API_DATETIME_FORMAT);
			}
		}

		const loadParams = {
			farmId,
			rangeType: range,
			...!isPlannedOrTodate(range) && { fromDate, toDate },
			is_current_customer_load: [9].includes(roleId),
			planTypeId: selectedPlanTypeId
		};
		getLogisticsHistorylist();
		dispatch(getLoadQueueCount(loadParams));
	}

	const handleRefreshToggle = (e) => {
		const isChecked = e.target.checked;
		const cb = (isSaved) => !isSaved && dispatch(userConfiguration("autoRefresh", { logistic_history: isChecked }));

		dispatch(userConfiguration("autoRefresh", { logistic_history: isChecked }));
		dispatch(updateConfiguration({
			configuration_name: "AutoRefresh",
			configuration: { logistic_history: isChecked }
		}, cb));

		if (isChecked) {
			onRefresh();
		};
	}

	const clearTimer = () => {
		if (timerId) clearInterval(timerId);
		timerId = null;
	}

	useEffect(() => {
		const roleId = getUserInfo().role_id;

		let fromDate = null, toDate = null;
		if (!isPlannedOrTodate(range)) {
			if (from && to) {
				fromDate = isToday(range) ? moment.utc(from).subtract(recentHours, "hours").format(API_DATETIME_FORMAT) :
					moment.utc(from).format(API_DATETIME_FORMAT);
				toDate = moment.utc(to).format(API_DATETIME_FORMAT);
			} else {
				const now = moment.utc().tz(timezone);
				fromDate = now.clone().startOf("day").format(API_DATETIME_FORMAT);
				toDate = now.clone().endOf("day").format(API_DATETIME_FORMAT);
			}
		}

		const loadParams = {
			farmId,
			rangeType: range,
			...!isPlannedOrTodate(range) && { fromDate, toDate },
			is_current_customer_load: [9].includes(roleId),
			planTypeId: selectedPlanTypeId
		};
		clearTimer();
		if (logisticHistoryRefresh && isToday(range) && selectedPlanTypeId === LOGISTIC_PLAN_TYPE_ID) {
			timerId = setInterval(onRefresh, 5 * 60 * 1000);
			dispatch(getLoadQueueCount(loadParams));
		}

		return clearTimer;
	}, [logisticHistoryRefresh, rangeId]);

	useEffect(() => {
		dispatch(getUserConfiguration("autoRefresh", { configuration_name: "AutoRefresh" }));
	}, []);

	return (
		<Col className="header title-details col-12">
			<Row>
				<Col className='col-auto pr-1'>
					<Heading5 className="d-inline title">Logistics History</Heading5>
				</Col>

				<Col className="pl-0 col-4">
					<span style={{ color: '#6c757d' }}> - </span>
					{selectedName && <span style={{ color: '#6c757d', fontSize: '15px' }}><b>{selectedName} </b> | </span>}
					<span style={{ color: '#6c757d', fontSize: '15px' }}>Logistics Trips: <b>{selectedViewType === 3 ? tareTotalCount : totalCount}</b></span> |
					<span style={{ color: '#6c757d', fontSize: '15px' }}> Total Weight: <b>{selectedViewType === 3 ? totalTareWeight : getNetTotal} {weightShortName}</b></span>
				</Col>

				<Col style={{ textAlign: "end" }}>
					<Row className="refresh-content">
						{isToday(range) && <>
							<span className="btn-refresh">
								<i
									title="Refresh"
									className="fa fa-refresh"
									aria-hidden="true"
									onClick={onRefresh}
								/>
							</span>
							<span className="label">Auto refresh</span>
							<ToggleSwitch
								className="switch"
								title={logisticHistoryRefresh ? "Disable auto refresh" : "Enable auto refresh"}
								onChange={(e) => handleRefreshToggle(e)}
								checked={logisticHistoryRefresh}
							/>
						</>
						}
						<Workbook filename={fileName}
							element={<Button size="sm" disabled={totalCount === 0} className="mr-2">
								<i className="fa fa-cloud-download" /> Download</Button>}>
							{getWorkSheets()}
						</Workbook>
					</Row>
				</Col>
			</Row>
		</Col>
	);
};

export default memo(LogisticsHeader);